import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  defaultAiTerminalSettings,
  mergePersistentWorkspaces,
  normalizeAiTerminalSettings,
  type AiTerminalSettings,
  type AiTerminalSettingsUpdate,
} from '../types/aiTerminalSettings';
import type { CrashReportMode } from '../features/crash-dialog/types/crashTypes';
import {
  normalizeToolbarCustomization,
  type ToolbarCustomization,
} from '../features/main-toolbar/model/toolbarCustomization';
import {
  type ThemeMode,
  resolveIsDarkMode,
  migrateLegacyDarkMode,
  trackThemeModeChange,
  watchSystemThemeChanges,
} from '../utils/darkMode';
import type { DatabaseEngineId } from '../core/engines/engineTypes';
import {
  normalizeBetaFeatureOverrides,
  type BetaFeatureId,
  type BetaFeatureOverrides,
} from '../core/beta/betaFeatures';
import type { SchemaCompareOptions } from '../features/workspace/core/types/workspaceTypes';
import {
  NUMERIC_GROUPING_MODES,
  type NumericGroupingMode,
} from '../features/workspace/common/utils/columnAlignment';
import {
  normalizeEditorColorScheme,
  type EditorColorSchemeId,
  type EditorColorSelection,
} from '../core/theme/monacoColorSchemes';
import { setMonacoEditorColorScheme } from '../core/theme/monacoTheme';
import {
  normalizeCustomEditorColors,
  seedCustomEditorColors,
  type CustomEditorColors,
} from '../core/theme/customEditorColors';
import { createGuardedJsonStorage } from '../core/storage/guardedJsonStorage';
import { showPersistWriteFailureToast } from '../components/ui/persistWriteFailureToast';
import type { StorageTransferSettings } from '../types/azureStorageTransfers';

export const defaultAzureStorageTransferSettings: StorageTransferSettings = {
  maxConcurrentJobs: 3,
  blockConcurrency: 8,
  blockSizeMiB: 8,
};

export function normalizeAzureStorageTransferSettings(settings: Partial<StorageTransferSettings> | undefined): StorageTransferSettings {
  const bounded = (value: number | undefined, fallback: number, min: number, max: number) => Math.min(max, Math.max(min, Math.round(Number.isFinite(value) ? value! : fallback)));
  return {
    maxConcurrentJobs: bounded(settings?.maxConcurrentJobs, defaultAzureStorageTransferSettings.maxConcurrentJobs, 1, 8),
    blockConcurrency: bounded(settings?.blockConcurrency, defaultAzureStorageTransferSettings.blockConcurrency, 1, 32),
    blockSizeMiB: bounded(settings?.blockSizeMiB, defaultAzureStorageTransferSettings.blockSizeMiB, 4, 100),
  };
}

function syncAzureStorageTransferSettings(settings: StorageTransferSettings): void {
  void window.electronAPI?.azureStorage?.transferSettings({ settings }).catch(() => { /* main may not expose S2 yet */ });
}

// PostgreSQL tools settings
export interface PgsqlToolsSettings {
  /** Custom path to PostgreSQL bin directory (containing pg_dump, pg_restore, psql) */
  customBinPath: string;
}

export const defaultPgsqlToolsSettings: PgsqlToolsSettings = {
  customBinPath: '',
};

// Query execution settings
export interface QueryExecutionSettings {
  /** Maximum time in seconds to wait for a query to complete. 0 = no timeout. */
  timeoutSeconds: number;
}

export const defaultQueryExecutionSettings: QueryExecutionSettings = {
  timeoutSeconds: 300,
};

// Spatial (map) settings — basemap tiles + optional custom style. `customStyleUrl` is validated
// http(s)-only before it is ever stored (see `isSafeStyleUrl`); it is never logged or tracked (it
// can carry an API key). `privacyNoticeAcknowledged` gates the one-time first-map-render notice.
export interface SpatialSettings {
  basemapEnabled: boolean;
  customStyleUrl: string | null;
  privacyNoticeAcknowledged: boolean;
}

export const defaultSpatialSettings: SpatialSettings = {
  basemapEnabled: true,
  customStyleUrl: null,
  privacyNoticeAcknowledged: false,
};

// Schema Compare "Save as my defaults" (W5.1, D4) — per-engine baseline compare
// options saved from the setup dialog's "Save as my defaults" button. Read via
// `getSchemaCompareDefaultOptions` below (the single accessor other call sites
// — `createDefaultSchemaCompareContent`, navigation-created tabs (W3.4), the
// depth default (W5.2) — should use rather than reaching into this map
// directly). A brand-new field: no persisted payload predates it, so no
// migration/shallow-merge is needed (a missing key on old payloads just keeps
// the `{}` initial state).
export type SchemaCompareDefaultOptionsByEngine = Partial<
  Record<DatabaseEngineId, SchemaCompareOptions>
>;

// Editor settings types
export type EditorWordWrap = 'on' | 'off';
export type EditorTabSize = 2 | 4 | 8;

export interface EditorSettings {
  wordWrap: EditorWordWrap;
  formatOnExecute: boolean;
  tabSize: EditorTabSize;
  /**
   * Show the floating Execute / Peek bar both next to a Monaco selection AND
   * above the statement under the cursor (when the editor contains 2+
   * statements). Off = no floating bar at all — instead, selection-runnable
   * SQL is surfaced through the toolbar Execute button (which relabels to
   * "Selection"), and the cursor-statement gets two small icon buttons
   * stacked to the left of the line numbers.
   */
  showInlineExecuteBar: boolean;
}

export const defaultEditorSettings: EditorSettings = {
  wordWrap: 'on',
  formatOnExecute: false,
  tabSize: 2,
  showInlineExecuteBar: false,
};

// Object Explorer grouping mode type
export type ObjectExplorerGroupingMode = 'auto' | 'always' | 'never';

// Tab orientation
export type TabOrientation = 'horizontal' | 'vertical';

// Expanded Value Dialog: JSON sub-view preference (Tree vs Code).
export type ExpandedJsonView = 'tree' | 'code';

// FK Breadcrumb (Slice 3): default behavior for the FK popover's "Open" button.
// - `in-place` (default): drill inside the current TE tab via the navigation tree.
// - `new-tab`: preserve the old "Open in Table Explorer" behavior — open a new TE tab.
// Settings dialog surfaces a dropdown to flip this permanently.
export type FkOpenDefault = 'in-place' | 'new-tab';

// PP-01 — how quantity columns in BOTH results grids render their thousands
// separators (display only; copy/export/edit always use the raw value). The
// mode's semantics live next to the formatter in `columnAlignment.ts`.
export type { NumericGroupingMode };

// Record Details (Single-Row View) toolbar prefs — persisted across visits.
export type RowDetailsDensity = 'compact' | 'normal';

// Startup with a saved session: 'ask' shows the Sessions dialog, 'new-session'
// skips it and lands on the Start Page (the saved session stays resumable via
// the toolbar Sessions button — nothing is restored and nothing is deleted).
// Surfaced as the "Always start a new session" checkbox on the startup dialog
// and in Settings → Sessions.
export type StartupSessionBehavior = 'ask' | 'new-session';

export const DEFAULT_VERTICAL_TABS_WIDTH = 192; // 12rem
export const MIN_VERTICAL_TABS_WIDTH = 144;     // 9rem
export const MAX_VERTICAL_TABS_WIDTH = 320;     // 20rem

// Theme tri-state (CH-11): `ThemeMode`, `resolveIsDarkMode`,
// `migrateLegacyDarkMode`, `trackThemeModeChange`, and `watchSystemThemeChanges`
// live in `src/utils/darkMode.ts` (imported above) — that file is also where
// the boot-time pre-paint follow-up (`initDarkMode`) lives, so the tri-state
// resolution logic has exactly one home. Only the store-specific glue
// (`syncDarkModeWithDom` below, plus the state/actions/listener wiring
// further down) stays here.

// Semantic Resolver settings
export interface ResolverSettings {
  /** Maximum number of ParsedBuffer entries cached per connection. LRU. Default 16. */
  maxCachedDocs: number;
}

export const defaultResolverSettings: ResolverSettings = {
  maxCachedDocs: 16,
};

// IntelliSense Engine (cross-engine, per PRD `Ralph/IntellisenseEngine/`).
// (The legacy `mssqlIntelliSense` SqlToolsService settings were removed in
// Plan 20 §10 step 7 — SSTS no longer serves mssql language features.)
export type KeywordCasingMode = 'match-user' | 'upper' | 'lower';
/**
 * Filter applied to version-incompatible completion suggestions.
 * - 'annotated' (default): show every candidate; demote unavailable / deprecated
 *   in ranking and append a right-aligned badge like `≥ 2017`.
 * - 'strict': drop unavailable candidates entirely; keep deprecated visible.
 * - 'off': pass through with no badges or demotion.
 */
export type AutocompleteVersionFilter = 'strict' | 'annotated' | 'off';

export interface IntellisenseSettings {
  /**
   * Master switch for every SQL-editor language feature. When false the
   * completion provider contributes nothing (Ctrl+Space included), Monaco's
   * word-based / hover / parameter-hint / inlay-hint options are off, no hover,
   * signature-help, inlay-hint or code-action provider registers, and every
   * diagnostic owner (`sql`, `sql-syntax`, `sql-typecheck`) is cleared and
   * stays cleared. The finer toggles below are read through
   * `src/features/intellisense/settings/intellisenseGates.ts`, which ANDs them
   * with this flag. Default true.
   */
  enabled: boolean;
  /**
   * Casing applied to keywords on insert.
   * - 'match-user' (default): case follows the user-typed fragment, fall back
   *   to the dominant case detected in the buffer.
   * - 'upper' / 'lower': force the case.
   */
  keywordCasing: KeywordCasingMode;
  /** See `AutocompleteVersionFilter`. Default `'annotated'`. */
  autocompleteVersionFilter: AutocompleteVersionFilter;
  /**
   * SQLite-only: when true, FTS5 / R*Tree shadow tables (e.g. `mytable_data`,
   * `mytable_idx`) are surfaced in completion. Default false. Wired up by the
   * SQLite Resolver — Engine Extras slice.
   */
  sqliteRevealShadowTables: boolean;
  /**
   * In-editor type-mismatch diagnostics (Item 3e). When true, the resolver's
   * coercion-aware pass squiggles comparisons that are a type error on the
   * connected engine — e.g. a numeric column compared to a non-numeric string
   * literal (`WHERE total = 'free'`). Conservative by design (only flags
   * mismatches that are a genuine bug on every engine); never fires without a
   * loaded schema snapshot. Default true. See Docs/TypeCheckDiagnostics.md.
   */
  typeMismatchDiagnostics: boolean;
  /**
   * Semantic type-on-hover (Item C3). When true, hovering a column reference
   * shows its declared data type + coarse category, and hovering a completed
   * function call shows its inferred result type — reusing the same inference
   * the operator suggester + type-mismatch diagnostics use. Additive only
   * (never squiggles); composes with the LSP / table-card hovers. Default true.
   */
  semanticTypeHover: boolean;
  /**
   * In-house live syntax diagnostics for MSSQL (Plan 20 step 2). When true, the
   * vendored `sql-grammar-tsql` (ANTLR) parser squiggles genuine syntax errors,
   * replacing SqlToolsService's `publishDiagnostics`. False-positive-first:
   * an error only marks where BOTH the ANTLR and tree-sitter grammars reject
   * the same span, never at EOF/mid-keystroke, one per GO batch. Turning it OFF
   * clears the `sql-syntax` markers AND keeps SSTS diagnostics dropped for
   * mssql (SSTS is not a fallback). Default true.
   * See Docs/SqlToolsService.md, Docs/TypeCheckDiagnostics.md.
   */
  liveSyntaxDiagnostics: boolean;
  /**
   * Safe-mode master gate for live diagnostics. When false, overrides the
   * finer per-feature toggles above and turns off every diagnostic marker
   * source at once — `sql-syntax` (liveSyntaxDiagnostics), `sql-typecheck`
   * (typeMismatchDiagnostics), and the language-server-published (LSP)
   * markers. Default true.
   */
  liveDiagnostics: boolean;
  /**
   * Safe-mode master gate for hover + signature help. When false, overrides
   * the finer per-feature toggles (e.g. semanticTypeHover) and disables
   * every hover and signature-help provider at once. Default true.
   */
  hoverAndSignatureHelp: boolean;
  /**
   * Safe-mode master gate for semantic completion. When false, the
   * resolver/orchestrator is skipped entirely and completion falls back to
   * word-based suggestions only. Default true.
   */
  semanticCompletion: boolean;
  /**
   * Automatic suggestion popup. When false, the suggest widget no longer opens
   * on its own while typing — Monaco's `quickSuggestions` and
   * `suggestOnTriggerCharacters` are off and the cold→warm schema retrigger is
   * suppressed — but Ctrl+Space still opens it on demand. This is the "turn
   * autocomplete off" switch users asked for; `semanticCompletion` only swaps
   * the schema-aware list for word-based suggestions. Default true.
   */
  autoSuggest: boolean;
}

export const defaultIntellisenseSettings: IntellisenseSettings = {
  enabled: true,
  keywordCasing: 'match-user',
  autocompleteVersionFilter: 'annotated',
  sqliteRevealShadowTables: false,
  typeMismatchDiagnostics: true,
  semanticTypeHover: true,
  liveSyntaxDiagnostics: true,
  liveDiagnostics: true,
  hoverAndSignatureHelp: true,
  semanticCompletion: true,
  autoSuggest: true,
};

// Visual Query Editor settings (consumed by VQE-Edit's commit pipeline).
export interface VisualQueryEditorSettings {
  /**
   * Re-run the SELECT under the compact Visual Query Editor after every
   * committed visual edit (filter, sort, column, join…), so the results grid
   * follows the query the way the Table Explorer does. SELECT only — never
   * INSERT/UPDATE/DELETE/MERGE. Default on.
   */
  runOnChange: boolean;
}

export const defaultVisualQueryEditorSettings: VisualQueryEditorSettings = {
  runOnChange: true,
};


function syncDarkModeWithDom(isDark: boolean): void {
  if (typeof document === 'undefined') return;

  // Sync the main process so it can (1) open the *next* launch's
  // BrowserWindow with a matching backgroundColor (no white flash) and
  // (2) apply the theme to the *current* live window right now —
  // backgroundColor + Windows titleBarOverlay (electron/main.ts's
  // `app:set-dark-mode-hint` handler). Called from setThemeMode/
  // toggleDarkMode, from the live system-preference listener below, and from
  // onRehydrateStorage, so every source of a theme change reaches the shell.
  // Fire-and-forget; no-op in web mode.
  if (typeof window !== 'undefined') {
    window.electronAPI?.appSettings?.setDarkModeHint?.(isDark).catch(() => {
      /* ignore — web mode or IPC unavailable */
    });
  }

  if (isDark) {
    document.documentElement.classList.add('dark');
    return;
  }

  document.documentElement.classList.remove('dark');
}

// Dedupe guard: onRehydrateStorage can re-run on HMR in dev; app:start should fire once per app launch.
let hasTrackedAppStart = false;

async function syncAnalyticsOnHydrate(
  analyticsEnabled: boolean,
  theme: 'dark' | 'light',
): Promise<void> {
  if (typeof window === 'undefined' || !window.electronAPI?.analytics) return;

  try {
    await window.electronAPI.analytics.setEnabled(analyticsEnabled);
  } catch {
    // Ignore IPC errors (web mode, transient failures)
  }

  if (hasTrackedAppStart) return;
  hasTrackedAppStart = true;

  try {
    await window.electronAPI.analytics.track('app:start', { theme });
  } catch {
    // Ignore IPC errors
  }
}

interface AppState {
  // Theme tri-state (CH-11). Persisted. `'system'` resolves live via
  // `isDarkMode` below rather than being written to disk as a boolean.
  themeMode: ThemeMode;
  /**
   * Derived from `themeMode` (resolves `'system'` via `prefers-color-scheme`).
   * Computed, NEVER persisted — only `themeMode` is (see `partialize`).
   */
  isDarkMode: boolean;

  /**
   * Syntax-highlighting color scheme for all Monaco editors (Settings → UI →
   * Editor colors). Each preset ships a light + dark variant; `isDarkMode`
   * picks which one is active. `'custom'` (Slice 3) picks the user's own
   * table from `customEditorColors` below instead of a preset. See
   * `core/theme/monacoColorSchemes.ts`.
   */
  editorColorScheme: EditorColorSelection;

  /**
   * The user's own editor token-color table (Settings → UI → Editor colors →
   * Custom), light + dark. `null` until the user first selects Custom, at
   * which point it's seeded from whatever preset was active. Read only when
   * `editorColorScheme === 'custom'`. See `core/theme/customEditorColors.ts`.
   */
  customEditorColors: CustomEditorColors | null;

  // Privacy settings
  analyticsEnabled: boolean;

  // Crash report behavior (ask / always / never)
  crashReportMode: CrashReportMode;

  // Editor settings (word wrap, format on execute)
  editorSettings: EditorSettings;

  // AI Workspace + Terminal settings
  aiTerminalSettings: AiTerminalSettings;

  // PostgreSQL tools settings
  pgsqlToolsSettings: PgsqlToolsSettings;

  // Query execution settings (timeout, etc.)
  queryExecutionSettings: QueryExecutionSettings;

  // Spatial (map) settings
  spatialSettings: SpatialSettings;
  azureStorageTransferSettings: StorageTransferSettings;

  /**
   * Beta features the user has explicitly turned on or off. Ids absent from
   * the map have never been decided and resolve to the registry default —
   * always read this through `isBetaFeatureEnabled` / `useBetaFeature`
   * (`src/core/beta`), never by indexing the map directly.
   */
  betaFeatures: BetaFeatureOverrides;

  // Schema Compare "Save as my defaults" (W5.1) — per-engine saved options.
  schemaCompareDefaultOptions: SchemaCompareDefaultOptionsByEngine;

  // What's New: last version the user has seen the changelog for
  lastSeenVersion: string | null;

  // Object Explorer grouping mode
  objectExplorerGrouping: ObjectExplorerGroupingMode;

  // Tab orientation (horizontal row vs. vertical column)
  tabOrientation: TabOrientation;
  verticalTabsWidth: number;

  // Semantic Resolver settings
  resolverSettings: ResolverSettings;

  // IntelliSense Engine cross-engine settings (keyword casing, etc.)
  intellisenseSettings: IntellisenseSettings;

  // Visual Query Editor settings (run-on-change, etc.)
  visualQueryEditorSettings: VisualQueryEditorSettings;

  // Expanded Value Dialog: persisted Tree vs. Code preference for JSON cells.
  expandedJsonView: ExpandedJsonView;

  // FK Breadcrumb (Slice 3)
  /** True after the user has seen the first-run hint toast at least once. */
  hasSeenFkBreadcrumbHint: boolean;
  /** Behavior of the FK popover's "Open" button. */
  fkOpenDefault: FkOpenDefault;

  /** Thousands separators for quantity columns in the results grids. */
  numericGrouping: NumericGroupingMode;

  /** What a startup with a saved session does — see `StartupSessionBehavior`. */
  startupSessionBehavior: StartupSessionBehavior;

  // Record Details (Single-Row View) toolbar prefs — survive across visits.
  /** `undefined` = use the wide-row auto-default; otherwise user's explicit choice. */
  rowDetailsHideNulls: boolean | undefined;
  rowDetailsDensity: RowDetailsDensity;
  rowDetailsGroupByCategory: boolean;

  /**
   * Generated INSERTs (results-grid "Copy INSERT" / "Export SQL (INSERT)")
   * leave IDENTITY columns out so the target assigns fresh IDs. Default false:
   * identity values are kept and MSSQL output is wrapped in
   * `SET IDENTITY_INSERT ON/OFF` — we never silently change IDs.
   */
  insertSkipIdentity: boolean;

  /**
   * The user's own main-toolbar layout (F01-P3), or `null` for "follow the
   * shipped defaults forever" — so a later default reshuffle still reaches
   * everyone who never customized, and a button that ships after a user
   * customized still appears for them. Stored as-persisted: the render path
   * and the Settings panel each normalize it, and the panel is what reports a
   * repair to the user. See `features/main-toolbar/model/toolbarCustomization`.
   */
  toolbarCustomization: ToolbarCustomization | null;

  // Actions
  setThemeMode: (mode: ThemeMode) => void;
  /** Flips the *effective* (resolved) theme between light/dark, overriding `'system'` if active. */
  toggleDarkMode: () => void;
  setEditorColorScheme: (scheme: EditorColorSelection) => void;
  /**
   * Atomically commits a drafted scheme selection + custom color table — the
   * Editor colors dialog's Confirm action (staged editing: nothing reaches
   * this store or Monaco while the dialog is open). Normalizes both
   * arguments, writes `editorColorScheme` + `customEditorColors` in one
   * `set()`, pushes exactly one `setMonacoEditorColorScheme` call, and is a
   * no-op when neither differs from the current state.
   */
  applyEditorColors: (selection: EditorColorSelection, customColors: CustomEditorColors | null) => void;
  setAnalyticsEnabled: (enabled: boolean) => void;
  setCrashReportMode: (mode: CrashReportMode) => void;
  setEditorSettings: (settings: Partial<EditorSettings>) => void;
  resetEditorSettings: () => void;
  setAiTerminalSettings: (settings: AiTerminalSettingsUpdate) => void;
  resetAiTerminalSettings: () => void;
  setPgsqlToolsSettings: (settings: Partial<PgsqlToolsSettings>) => void;
  resetPgsqlToolsSettings: () => void;
  setQueryExecutionSettings: (settings: Partial<QueryExecutionSettings>) => void;
  resetQueryExecutionSettings: () => void;
  setSpatialSettings: (settings: Partial<SpatialSettings>) => void;
  setAzureStorageTransferSettings: (settings: Partial<StorageTransferSettings>) => void;
  /** Record an explicit opt-in / opt-out for one beta feature. */
  setBetaFeature: (id: BetaFeatureId, enabled: boolean) => void;
  /** Clear every explicit choice so all beta features follow their default again. */
  resetBetaFeatures: () => void;
  /** Saves `options` as the default for fresh `engine` compares (overwrites any prior save for that engine). */
  setSchemaCompareDefaultOptions: (engine: DatabaseEngineId, options: SchemaCompareOptions) => void;
  setLastSeenVersion: (version: string) => void;
  setObjectExplorerGrouping: (mode: ObjectExplorerGroupingMode) => void;
  setTabOrientation: (orientation: TabOrientation) => void;
  setVerticalTabsWidth: (px: number) => void;
  setResolverSettings: (settings: Partial<ResolverSettings>) => void;
  resetResolverSettings: () => void;
  setIntellisenseSettings: (settings: Partial<IntellisenseSettings>) => void;
  resetIntellisenseSettings: () => void;
  setVisualQueryEditorSettings: (settings: Partial<VisualQueryEditorSettings>) => void;
  resetVisualQueryEditorSettings: () => void;
  setExpandedJsonView: (view: ExpandedJsonView) => void;
  setHasSeenFkBreadcrumbHint: (seen: boolean) => void;
  setFkOpenDefault: (mode: FkOpenDefault) => void;
  setNumericGrouping: (mode: NumericGroupingMode) => void;
  setStartupSessionBehavior: (behavior: StartupSessionBehavior) => void;
  setRowDetailsHideNulls: (hide: boolean) => void;
  setRowDetailsDensity: (density: RowDetailsDensity) => void;
  setRowDetailsGroupByCategory: (grouped: boolean) => void;
  setInsertSkipIdentity: (skip: boolean) => void;
  /** Normalizes before storing, so the UI can never persist an illegal layout. */
  setToolbarCustomization: (next: ToolbarCustomization | null) => void;
}

/**
 * The persisted-store `migrate` step, extracted to a top-level named export
 * so it's directly unit-testable without exercising zustand's persist
 * rehydration pipeline. Each `fromVersion < N` block is a one-time,
 * additive shape fix — never remove an old block once shipped.
 */
export function migrateAppStorePersistedState(persisted: unknown, fromVersion: number): unknown {
  if (!persisted || typeof persisted !== 'object') return persisted;
  const next = { ...(persisted as Record<string, unknown>) };
  if (fromVersion < 1) {
    delete next.expandedJsonView;
  }
  if (fromVersion < 2) {
    const qe = next.queryExecutionSettings as
      | { timeoutSeconds?: number }
      | undefined;
    // Only rewrite the legacy default — anyone who explicitly picked a
    // different value (including 0 = no timeout) keeps their choice.
    if (qe && qe.timeoutSeconds === 15) {
      next.queryExecutionSettings = {
        ...qe,
        timeoutSeconds: defaultQueryExecutionSettings.timeoutSeconds,
      };
    }
  }
  if (fromVersion < 3) {
    next.themeMode = migrateLegacyDarkMode(next.darkMode);
    delete next.darkMode;
  }
  return next;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Initial state. Fresh installs default to `'system'` — an unopinionated
      // default that actually follows the OS, unlike a hardcoded light/dark.
      themeMode: 'system',
      isDarkMode: resolveIsDarkMode('system'),
      editorColorScheme: 'default',
      customEditorColors: null,
      analyticsEnabled: true, // Default to enabled
      crashReportMode: 'ask',
      editorSettings: defaultEditorSettings,
      aiTerminalSettings: defaultAiTerminalSettings,
      pgsqlToolsSettings: defaultPgsqlToolsSettings,
      queryExecutionSettings: defaultQueryExecutionSettings,
      spatialSettings: defaultSpatialSettings,
      azureStorageTransferSettings: defaultAzureStorageTransferSettings,
      betaFeatures: {},
      schemaCompareDefaultOptions: {},
      lastSeenVersion: null,
      objectExplorerGrouping: 'auto',
      tabOrientation: 'horizontal',
      verticalTabsWidth: DEFAULT_VERTICAL_TABS_WIDTH,
      resolverSettings: defaultResolverSettings,
      intellisenseSettings: defaultIntellisenseSettings,
      visualQueryEditorSettings: defaultVisualQueryEditorSettings,
      expandedJsonView: 'code',
      hasSeenFkBreadcrumbHint: false,
      fkOpenDefault: 'in-place',
      numericGrouping: 'locale',
      startupSessionBehavior: 'ask',
      rowDetailsHideNulls: undefined,
      rowDetailsDensity: 'normal',
      rowDetailsGroupByCategory: true,
      insertSkipIdentity: false,
      toolbarCustomization: null,

      // Actions
      setThemeMode: (mode: ThemeMode) => {
        if (get().themeMode === mode) return; // no-op: not a real change, no track/sync
        const isDark = resolveIsDarkMode(mode);
        syncDarkModeWithDom(isDark);
        trackThemeModeChange(mode);
        set({ themeMode: mode, isDarkMode: isDark });
      },
      toggleDarkMode: () => {
        const nextIsDark = !get().isDarkMode;
        const mode: ThemeMode = nextIsDark ? 'dark' : 'light';
        syncDarkModeWithDom(nextIsDark);
        trackThemeModeChange(mode);
        set({ themeMode: mode, isDarkMode: nextIsDark });
      },
      setEditorColorScheme: (scheme: EditorColorSelection) => {
        const previous = get().editorColorScheme;
        if (previous === scheme) return; // no-op: not a real change

        if (scheme === 'custom' && get().customEditorColors === null) {
          // First time the user reaches Custom: start from whatever preset
          // they were looking at (falling back to 'default' if they were
          // somehow already on 'custom' with no table — defensive only).
          const seedFrom: EditorColorSchemeId = previous === 'custom' ? 'default' : previous;
          const seeded = seedCustomEditorColors(seedFrom);
          setMonacoEditorColorScheme(scheme, seeded);
          set({ editorColorScheme: scheme, customEditorColors: seeded });
          return;
        }

        setMonacoEditorColorScheme(scheme, scheme === 'custom' ? get().customEditorColors : undefined);
        set({ editorColorScheme: scheme });
      },
      applyEditorColors: (selection, customColors) => {
        const normalizedSelection = normalizeEditorColorScheme(selection);
        const normalizedCustomColors = customColors === null ? null : normalizeCustomEditorColors(customColors);

        const { editorColorScheme, customEditorColors } = get();
        const unchanged =
          editorColorScheme === normalizedSelection &&
          JSON.stringify(customEditorColors) === JSON.stringify(normalizedCustomColors);
        if (unchanged) return;

        setMonacoEditorColorScheme(normalizedSelection, normalizedCustomColors);
        set({ editorColorScheme: normalizedSelection, customEditorColors: normalizedCustomColors });
      },
      setAnalyticsEnabled: (enabled: boolean) => {
        // Sync to main process
        if (typeof window !== 'undefined' && window.electronAPI?.analytics?.setEnabled) {
          window.electronAPI.analytics.setEnabled(enabled).catch(() => {
            // Ignore errors in web mode or if IPC fails
          });
        }
        set({ analyticsEnabled: enabled });
      },
      setCrashReportMode: (mode) => set({ crashReportMode: mode }),
      setEditorSettings: (settings) =>
        set((state) => ({
          editorSettings: {
            ...state.editorSettings,
            ...settings,
          },
        })),
      resetEditorSettings: () =>
        set({
          editorSettings: defaultEditorSettings,
        }),
      setAiTerminalSettings: (settings) =>
        set((state) => ({
          aiTerminalSettings: normalizeAiTerminalSettings({
            ...state.aiTerminalSettings,
            ...settings,
            aiWorkspace: {
              ...state.aiTerminalSettings.aiWorkspace,
              ...(settings.aiWorkspace ?? {}),
            },
            history: {
              ...state.aiTerminalSettings.history,
              ...(settings.history ?? {}),
            },
            agentContext: {
              ...state.aiTerminalSettings.agentContext,
              ...(settings.agentContext ?? {}),
            },
            autoLaunch: {
              ...state.aiTerminalSettings.autoLaunch,
              ...(settings.autoLaunch ?? {}),
            },
            nudges: {
              ...state.aiTerminalSettings.nudges,
              ...(settings.nudges ?? {}),
            },
            // A Record, not a flat section: an update names only the
            // connections it changes (and uses `null` to de-register one), so
            // the generic `...settings` spread above would REPLACE the whole
            // map and silently drop every other connection's kept workspace.
            // This branch is load-bearing from its first field — see
            // `appStore.test.ts` → "persistentWorkspaces section".
            persistentWorkspaces: mergePersistentWorkspaces(
              state.aiTerminalSettings.persistentWorkspaces,
              settings.persistentWorkspaces
            ),
          }),
        })),
      // M-3 (AI smoothness wave R4-c): `persistentWorkspaces` is a record of
      // WHERE the user's data lives (kept AI workspace folders + their
      // `.history/`), not a preference — it must survive "Reset to
      // defaults" untouched. A bare `defaultAiTerminalSettings` replacement
      // would wipe the registry back to `{}` while the on-disk
      // `.jam-persistent` marker and folder contents stay put, orphaning
      // them with no in-app way to find or reclaim them. Every other
      // section still resets for real.
      resetAiTerminalSettings: () =>
        set((state) => ({
          aiTerminalSettings: {
            ...defaultAiTerminalSettings,
            persistentWorkspaces: state.aiTerminalSettings.persistentWorkspaces,
          },
        })),
      setPgsqlToolsSettings: (settings) =>
        set((state) => ({
          pgsqlToolsSettings: {
            ...state.pgsqlToolsSettings,
            ...settings,
          },
        })),
      resetPgsqlToolsSettings: () =>
        set({
          pgsqlToolsSettings: defaultPgsqlToolsSettings,
        }),
      setQueryExecutionSettings: (settings) =>
        set((state) => ({
          queryExecutionSettings: {
            ...state.queryExecutionSettings,
            ...settings,
          },
        })),
      resetQueryExecutionSettings: () =>
        set({
          queryExecutionSettings: defaultQueryExecutionSettings,
        }),
      setSpatialSettings: (settings) =>
        set((state) => ({
          spatialSettings: {
            ...state.spatialSettings,
            ...settings,
          },
        })),
      setAzureStorageTransferSettings: (settings) => {
        const next = normalizeAzureStorageTransferSettings({ ...get().azureStorageTransferSettings, ...settings });
        set({ azureStorageTransferSettings: next });
        if (typeof window !== 'undefined') syncAzureStorageTransferSettings(next);
      },
      setBetaFeature: (id, enabled) =>
        set((state) => ({
          betaFeatures: { ...state.betaFeatures, [id]: enabled },
        })),
      resetBetaFeatures: () => set({ betaFeatures: {} }),
      setSchemaCompareDefaultOptions: (engine, options) =>
        set((state) => ({
          schemaCompareDefaultOptions: {
            ...state.schemaCompareDefaultOptions,
            [engine]: options,
          },
        })),
      setLastSeenVersion: (version: string) =>
        set({ lastSeenVersion: version }),
      setObjectExplorerGrouping: (mode) =>
        set({ objectExplorerGrouping: mode }),
      setTabOrientation: (orientation) => set({ tabOrientation: orientation }),
      setVerticalTabsWidth: (px) =>
        set({
          verticalTabsWidth: Math.max(
            MIN_VERTICAL_TABS_WIDTH,
            Math.min(MAX_VERTICAL_TABS_WIDTH, Math.round(px)),
          ),
        }),
      setResolverSettings: (settings) =>
        set((state) => ({
          resolverSettings: {
            ...state.resolverSettings,
            ...settings,
          },
        })),
      resetResolverSettings: () =>
        set({ resolverSettings: defaultResolverSettings }),
      setIntellisenseSettings: (settings) =>
        set((state) => ({
          intellisenseSettings: {
            ...state.intellisenseSettings,
            ...settings,
          },
        })),
      resetIntellisenseSettings: () =>
        set({ intellisenseSettings: defaultIntellisenseSettings }),
      setVisualQueryEditorSettings: (settings) =>
        set((state) => ({
          visualQueryEditorSettings: {
            ...state.visualQueryEditorSettings,
            ...settings,
          },
        })),
      resetVisualQueryEditorSettings: () =>
        set({ visualQueryEditorSettings: defaultVisualQueryEditorSettings }),
      setExpandedJsonView: (view) => set({ expandedJsonView: view }),
      setHasSeenFkBreadcrumbHint: (seen) => set({ hasSeenFkBreadcrumbHint: seen }),
      setFkOpenDefault: (mode) => set({ fkOpenDefault: mode }),
      setNumericGrouping: (mode) => set({ numericGrouping: mode }),
      setStartupSessionBehavior: (behavior) => set({ startupSessionBehavior: behavior }),
      setRowDetailsHideNulls: (hide) => set({ rowDetailsHideNulls: hide }),
      setRowDetailsDensity: (density) => set({ rowDetailsDensity: density }),
      setRowDetailsGroupByCategory: (grouped) => set({ rowDetailsGroupByCategory: grouped }),
      setInsertSkipIdentity: (skip) => set({ insertSkipIdentity: skip }),
      setToolbarCustomization: (next) =>
        set({ toolbarCustomization: normalizeToolbarCustomization(next).customization }),
    }),
    {
      name: 'sql-studio-app-store',
      storage: createGuardedJsonStorage({ onFailure: showPersistWriteFailureToast('your settings', { plural: true }) }),
      // Bump when persisted shape needs a one-time reset for shipped users.
      // v1: drop the legacy `expandedJsonView: 'tree'` default so users land
      // on the new Code default instead of inheriting the old default they
      // never explicitly chose.
      // v2: lift the legacy 15 s query timeout to the new 300 s default for
      // users who never explicitly customised it — 15 s was the *old* default
      // and it kills any multi-batch CREATE OR ALTER script.
      // v3: replace the boolean `darkMode` with the tri-state `themeMode`
      // (CH-11) — see `migrateLegacyDarkMode` above.
      version: 3,
      migrate: migrateAppStorePersistedState,
      merge: (persisted, current) => {
        const persistedState = (persisted ?? {}) as Partial<AppState>;

        // Guards a garbage/foreign-machine value (settings-bundle import,
        // hand-edited storage) rather than trusting the persisted string.
        const editorColorScheme = normalizeEditorColorScheme(persistedState.editorColorScheme);
        // Same guard for the custom table itself, per-token — see
        // `normalizeCustomEditorColors`.
        let customEditorColors = normalizeCustomEditorColors(persistedState.customEditorColors);
        // A 'custom' selection with no table (e.g. imported from a bundle
        // that dropped it, or a hand-edited payload) falls back to seeding
        // from 'default' rather than leaving Monaco with nothing to render.
        if (editorColorScheme === 'custom' && customEditorColors === null) {
          customEditorColors = seedCustomEditorColors('default');
        }

        // PP-01 — an unknown mode (hand-edited bundle, a future app version's
        // value) would leave the Settings select and the context-menu radio
        // group with nothing selected and feed an arbitrary `from` into
        // analytics; fall back to the current default instead.
        const numericGrouping = NUMERIC_GROUPING_MODES.includes(
          persistedState.numericGrouping as NumericGroupingMode,
        )
          ? (persistedState.numericGrouping as NumericGroupingMode)
          : current.numericGrouping;

        return {
          ...current,
          ...persistedState,
          editorColorScheme,
          customEditorColors,
          numericGrouping,
          // Nested settings objects must be shallow-merged against current
          // defaults so newly added fields (added in later app versions) keep
          // their default value for users whose persisted payload predates the
          // field — otherwise the persisted spread leaves them `undefined`.
          editorSettings: {
            ...current.editorSettings,
            ...(persistedState.editorSettings ?? {}),
          },
          aiTerminalSettings: normalizeAiTerminalSettings(
            persistedState.aiTerminalSettings ?? current.aiTerminalSettings
          ),
          // Shallow-merge so newly added IntelliSense settings keep their
          // default value for users whose persisted payload predates the field.
          intellisenseSettings: {
            ...current.intellisenseSettings,
            ...(persistedState.intellisenseSettings ?? {}),
          },
          // Field by field, not a spread: a payload from an older version can
          // carry the removed `previewThresholdBytes`, which must not be kept
          // (and re-persisted) forever.
          visualQueryEditorSettings: {
            runOnChange:
              persistedState.visualQueryEditorSettings?.runOnChange ??
              current.visualQueryEditorSettings.runOnChange,
          },
          // Shallow-merge so users whose persisted payload predates a spatial field keep the
          // default for that field instead of an `undefined` from the persisted spread.
          spatialSettings: {
            ...current.spatialSettings,
            ...(persistedState.spatialSettings ?? {}),
          },
          azureStorageTransferSettings: normalizeAzureStorageTransferSettings(persistedState.azureStorageTransferSettings),
          // Only explicit choices are persisted, so there is nothing to merge
          // against defaults — but a payload can arrive from an export made by
          // a different app version, carrying ids this build no longer knows.
          betaFeatures: normalizeBetaFeatureOverrides(persistedState.betaFeatures),
        };
      },
      partialize: (state) => ({
        themeMode: state.themeMode,
        editorColorScheme: state.editorColorScheme,
        customEditorColors: state.customEditorColors,
        analyticsEnabled: state.analyticsEnabled,
        crashReportMode: state.crashReportMode,
        editorSettings: state.editorSettings,
        aiTerminalSettings: state.aiTerminalSettings,
        pgsqlToolsSettings: state.pgsqlToolsSettings,
        queryExecutionSettings: state.queryExecutionSettings,
        spatialSettings: state.spatialSettings,
        azureStorageTransferSettings: state.azureStorageTransferSettings,
        betaFeatures: state.betaFeatures,
        schemaCompareDefaultOptions: state.schemaCompareDefaultOptions,
        lastSeenVersion: state.lastSeenVersion,
        objectExplorerGrouping: state.objectExplorerGrouping,
        tabOrientation: state.tabOrientation,
        verticalTabsWidth: state.verticalTabsWidth,
        resolverSettings: state.resolverSettings,
        intellisenseSettings: state.intellisenseSettings,
        visualQueryEditorSettings: state.visualQueryEditorSettings,
        expandedJsonView: state.expandedJsonView,
        hasSeenFkBreadcrumbHint: state.hasSeenFkBreadcrumbHint,
        fkOpenDefault: state.fkOpenDefault,
        numericGrouping: state.numericGrouping,
        startupSessionBehavior: state.startupSessionBehavior,
        rowDetailsHideNulls: state.rowDetailsHideNulls,
        rowDetailsDensity: state.rowDetailsDensity,
        rowDetailsGroupByCategory: state.rowDetailsGroupByCategory,
        insertSkipIdentity: state.insertSkipIdentity,
        toolbarCustomization: state.toolbarCustomization,
      }),
      onRehydrateStorage: () => {
        // Sync state with external systems after rehydration
        return (state: AppState | undefined) => {
          // `isDarkMode` is never persisted (see `partialize`) — the
          // in-memory value on `state` here is still whatever the initial
          // `create()` state computed (from `'system'`), so it must be
          // recomputed from the just-rehydrated `themeMode` and written back
          // explicitly. This is the one place that's guaranteed to run after
          // migrate+merge regardless of which path rehydration took.
          const mode: ThemeMode = state?.themeMode ?? 'system';
          const isDark = resolveIsDarkMode(mode);
          syncDarkModeWithDom(isDark);
          useAppStore.setState({ isDarkMode: isDark });

          // Push the rehydrated scheme (+ custom table, if any) into Monaco's
          // module-level "current" state so whichever editor mounts first in
          // this session already installs the right colors, rather than the
          // hardcoded default.
          setMonacoEditorColorScheme(state?.editorColorScheme ?? 'default', state?.customEditorColors ?? null);

          // Sync analytics-enabled state to main, then fire the deferred
          // `app:start` event carrying the fully-hydrated config (theme, ...).
          // We sequence setEnabled before track so that an opted-out user's
          // app:start is suppressed in main.
          const analyticsEnabled = state?.analyticsEnabled ?? true;
          const theme: 'dark' | 'light' = isDark ? 'dark' : 'light';
          void syncAnalyticsOnHydrate(analyticsEnabled, theme);
          if (typeof window !== 'undefined') syncAzureStorageTransferSettings(state?.azureStorageTransferSettings ?? defaultAzureStorageTransferSettings);
        };
      },
    }
  )
);

/**
 * Schema Compare "Save as my defaults" (W5.1) — THE accessor for reading a
 * saved per-engine default. Call this rather than reaching into
 * `useAppStore.getState().schemaCompareDefaultOptions` directly, so every
 * future call site shares one lookup path: `createDefaultSchemaCompareContent`
 * (this task, `features/workspace/core/types/workspaceTypes.ts`),
 * navigation-created tabs (W3.4, `schemaCompareNavigation.ts`), and the depth
 * default (W5.2) should all import and call this.
 *
 * Plain function (not a hook) — safe to call from non-React contexts. Returns
 * `undefined` when `engine` is unknown/undefined or nothing has been saved
 * for it yet; callers fall back to their own hardcoded baseline in that case.
 */
export function getSchemaCompareDefaultOptions(
  engine: DatabaseEngineId | undefined,
): SchemaCompareOptions | undefined {
  if (!engine) return undefined;
  return useAppStore.getState().schemaCompareDefaultOptions[engine];
}

// Live "system" theme follow-through (CH-11). `watchSystemThemeChanges`
// (src/utils/darkMode.ts) registers ONE listener for the life of the app —
// called once here rather than on every `setThemeMode` call, so switching
// modes repeatedly never accumulates duplicate listeners. `shouldFollow`
// no-ops the callback whenever `themeMode` isn't `'system'`, which is
// cheaper and safer than adding/removing the listener on every mode change.
watchSystemThemeChanges(
  () => useAppStore.getState().themeMode === 'system',
  (isDark) => {
    syncDarkModeWithDom(isDark);
    useAppStore.setState({ isDarkMode: isDark });
    // No `trackThemeModeChange` here — the *mode* is still `'system'`, only
    // the resolved OS preference flipped. Analytics tracks mode changes, not
    // every repaint of an already-selected mode.
  },
);

// Test-only hooks: expose getState/setState to E2E tests when running under
// SQL_STUDIO_TEST=1. Never exposed in production.
if (typeof window !== 'undefined' && window.electronAPI?.testMode?.isTestMode?.()) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__SQL_STUDIO_APP_STORE_GET__ = () => useAppStore.getState();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__SQL_STUDIO_APP_STORE_SET__ = (patch: Partial<AppState>) =>
    useAppStore.setState(patch);
}
